"use client";

import { useCallback, useEffect, useRef } from "react";
import { useTranslations } from "@zapier/i18n";
import {
  useBulkCheckDeletePermissions,
  type ZapDeleteMetadata,
} from "@zapier/client";
import { getAssetTypeLabel, ZAP_ASSET_ID_PREFIX } from "@zapier/utils";
import type { BulkSelectActionDisabledState } from "../components/common/Table/BulkActions/BulkActions";

const DELETE_PERMISSION_CHECK_DEBOUNCE_MS = 300;
const ZAP_ONLY_PREFIXES = [ZAP_ASSET_ID_PREFIX];

interface Input {
  getZapDeleteMetadata: (typedIds: string[]) => ZapDeleteMetadata[];
  /** Defaults to Zaps for the Zap-only tables; Folder passes unified-trash prefixes. */
  allowedAssetIdPrefixes?: string[];
}

/** Backs bulk delete actions for selected supported assets. */
export function useZapBulkDeleteState({
  getZapDeleteMetadata,
  allowedAssetIdPrefixes = ZAP_ONLY_PREFIXES,
}: Input) {
  const t = useTranslations("bulk-delete-modal");
  const { check, nonDeletableIds, hasChecked, isLoading } =
    useBulkCheckDeletePermissions();
  const isSupportedSelection = useCallback(
    (typedIds: string[]) =>
      typedIds.length > 0 &&
      typedIds.every((id) =>
        allowedAssetIdPrefixes.some((prefix) => id.startsWith(prefix)),
      ),
    [allowedAssetIdPrefixes],
  );

  const checkReference = useRef(check);
  checkReference.current = check;
  const getZapDeleteMetadataReference = useRef(getZapDeleteMetadata);
  getZapDeleteMetadataReference.current = getZapDeleteMetadata;
  const timeoutReference = useRef<ReturnType<typeof setTimeout>>();
  const selectedTypedIdsReference = useRef<string[]>();

  useEffect(() => () => clearTimeout(timeoutReference.current), []);

  const checkSelection = useCallback(
    (typedIds: string[]) => {
      clearTimeout(timeoutReference.current);
      if (!isSupportedSelection(typedIds)) return;

      timeoutReference.current = setTimeout(() => {
        checkReference.current(typedIds, {
          zaps: getZapDeleteMetadataReference.current(typedIds),
        });
      }, DELETE_PERMISSION_CHECK_DEBOUNCE_MS);
    },
    [isSupportedSelection],
  );

  const onSelectionChange = useCallback(
    (typedIds: string[]) => {
      selectedTypedIdsReference.current = typedIds;
      checkSelection(typedIds);
    },
    [checkSelection],
  );

  // Search rows receive storage metadata after selection. Recheck the current
  // selection when that metadata changes so permission state can't stay stale.
  useEffect(() => {
    const selectedTypedIds = selectedTypedIdsReference.current;
    if (selectedTypedIds) {
      checkSelection(selectedTypedIds);
    }
  }, [checkSelection, getZapDeleteMetadata]);

  const getDisabledState = useCallback(
    (typedIds: string[]): BulkSelectActionDisabledState => {
      if (!isSupportedSelection(typedIds) || !hasChecked || isLoading) {
        return { disabled: false };
      }

      const nonDeletableIdSet = new Set(nonDeletableIds);
      const isNoneDeletable = typedIds.every((id) => nonDeletableIdSet.has(id));
      if (!isNoneDeletable) {
        return { disabled: false };
      }

      return {
        disabled: true,
        tooltipText: t("buttons.delete-permission-denied-tooltip", {
          count: typedIds.length,
          assetTypeLabel: getAssetTypeLabel(typedIds),
        }),
      };
    },
    [hasChecked, isLoading, isSupportedSelection, nonDeletableIds, t],
  );

  return { onSelectionChange, getDisabledState };
}
